import { StateCreator } from "zustand";
import { playAlertSound } from "@/lib/audio";

/** Detection types matching the backend enum */
export type DetectionType = "unknown" | "wifi" | "ble";

/**
 * Detection interface matching the Go backend struct:
 * - Required fields: id, sensor_id, detection_time, latitude, longitude, detection_type, raw_detection, received_at
 * - Optional fields are nullable (use null instead of undefined for JSON compatibility)
 */
export interface Detection {
  id: string;
  sensor_id: string;
  detection_time: string;
  latitude: number | null;
  longitude: number | null;
  location_valid: boolean;
  drone_identifier: string | null;
  operator_id: string | null;
  altitude_msl: number | null;
  altitude_agl: number | null;
  speed_ms: number | null;
  heading_deg: number | null;
  mac_address: string | null;
  source: string | null;
  rssi: number | null;
  detection_type: DetectionType;
  raw_detection: unknown;
  received_at: string;
  h3_index: string;
  confidence_score?: number;
  confidence_factors?: string[];
}

/** Maximum unique tracks to keep in memory. */
const MAX_TRACKS = 2_000;

export interface DetectionSlice {
  trackedDroneId: string | null;
  trackedDrone: Detection | null;
  selectedDetectionId: string | null;
  selectedDetection: Detection | null;
  detections: Detection[];
  drones: Set<string>;
  alertsEnabled: boolean;
  setTrackedDroneId: (id: string | null) => void;
  setSelectedDetectionId: (id: string | null) => void;
  setSelectedDetection: (detection: Detection | null) => void;
  hydrateDetections: (detections: Detection[]) => void;
  addDrone: (id: string | null) => void;
  addDetection: (d: Detection) => void;
  reset: () => void;
  toggleAlerts: () => void;
}

export const createDetectionSlice: StateCreator<DetectionSlice> = (
  set,
  get
) => ({
  detections: [],
  drones: new Set<string>(),
  trackedDroneId: null,
  trackedDrone: null,
  selectedDetectionId: null,
  selectedDetection: null,
  alertsEnabled: true,
  setTrackedDroneId: (id) => {
    set(() => ({ trackedDroneId: id }));
  },
  setSelectedDetectionId: (id) => {
    set((state) => ({
      selectedDetectionId: id,
      selectedDetection: id
        ? state.detections.find((d) => d.id === id) ?? null
        : null,
    }));
  },
  setSelectedDetection: (detection) => {
    set(() => ({
      selectedDetectionId: detection?.id ?? null,
      selectedDetection: detection,
    }));
  },
  hydrateDetections: (detections) => {
    const byTrack = new Map<string, Detection>();
    for (const detection of detections) {
      const key = detection.drone_identifier || detection.id;
      byTrack.set(key, detection);
    }
    const bounded = [...byTrack.values()].slice(-MAX_TRACKS);
    const drones = new Set<string>();

    for (const detection of bounded) {
      if (detection.drone_identifier) {
        drones.add(detection.drone_identifier);
      }
    }

    set((state) => ({
      detections: bounded,
      drones,
      selectedDetection:
        bounded.find((d) => d.id === state.selectedDetectionId) ?? null,
      trackedDrone:
        state.trackedDroneId != null
          ? bounded.find((d) => d.drone_identifier === state.trackedDroneId) ??
            state.trackedDrone
          : null,
    }));
  },
  addDrone: (id) => {
    // Ignore null/undefined drone identifiers
    if (!id) return;
    set((state) => ({ drones: new Set(state.drones).add(id) }));
  },
  addDetection: (d) => {
    const state = get();
    const droneId = d.drone_identifier;

    // Play alert sound if alerts are enabled and it's a new drone
    if (droneId && state.alertsEnabled && !state.drones.has(droneId)) {
      playAlertSound();
    }

    set((state) => {
      const trackKey = droneId || d.id;
      const byTrack = new Map<string, Detection>();
      for (const existing of state.detections) {
        byTrack.set(existing.drone_identifier || existing.id, existing);
      }
      byTrack.set(trackKey, d);
      const newDetections = [...byTrack.values()].slice(-MAX_TRACKS);

      return {
        detections: newDetections,
        trackedDrone:
          droneId && droneId === state.trackedDroneId ? d : state.trackedDrone,
        selectedDetection:
          d.id === state.selectedDetectionId ? d : state.selectedDetection,
      };
    });
  },
  reset: () => {
    set(() => ({
      detections: [],
      drones: new Set<string>(),
      trackedDrone: null,
      trackedDroneId: null,
      selectedDetection: null,
      selectedDetectionId: null,
    }));
  },
  toggleAlerts: () => {
    set((state) => ({ alertsEnabled: !state.alertsEnabled }));
  },
});
